import { HierarchicalDimensionOption } from './types';
import {
  QBODimensionDefinition,
  QBODimensionValue,
} from 'services/integrations/quickbooks';

// Re-export types from quickbooks service for convenience
export type {
  QBODimensionDefinition,
  QBODimensionValue,
} from 'services/integrations/quickbooks';

/**
 * Transform QuickBooks API response into HierarchicalDimensionOption format
 *
 * Takes the response from fetchQBODimensions() which includes:
 * - definitions: Array of dimension types (Channel, Department, etc.)
 * - allValues: Array of all dimension values with their hierarchy
 *
 * Returns an array of dimensions ready for the DimensionsAutocomplete component
 */
export const transformQBODimensionsResponse = (response: {
  definitions: QBODimensionDefinition[];
  allValues: QBODimensionValue[];
}): HierarchicalDimensionOption[] => {
  const { definitions, allValues } = response;

  // Group values by definitionId
  const dimensionMap = new Map<string, QBODimensionValue[]>();
  allValues.forEach((value) => {
    if (!dimensionMap.has(value.definitionId)) {
      dimensionMap.set(value.definitionId, []);
    }
    dimensionMap.get(value.definitionId)!.push(value);
  });

  // Convert to HierarchicalDimensionOption format
  return definitions
    .filter((def) => def.active)
    .map((definition) => {
      const values = dimensionMap.get(definition.id) || [];
      const maxLevel =
        values.length > 0 ? Math.max(...values.map((v) => v.level)) : 0;

      const selectedValues: { [level: number]: string | null } = {};
      for (let i = 0; i <= maxLevel; i++) {
        selectedValues[i] = null;
      }

      return {
        id: definition.id,
        name: definition.label,
        type: definition.label,
        required: false,
        maxLevel,
        levelLabels: Array.from(
          { length: maxLevel + 1 },
          (_, i) => `Level ${i}`
        ),
        hierarchy: values.map((v) => ({
          id: v.id,
          name: v.label,
          level: v.level,
          parentId: v.parentId,
          definitionId: v.definitionId,
          fullyQualifiedLabel: v.fullyQualifiedLabel,
          active: v.active,
        })),
        selectedValues,
      };
    });
};
